import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { EmptyState } from '../ui/empty-state';
import { Users, TrendingUp, TrendingDown, Award } from 'lucide-react';

const AgentPerformanceMatrix = ({ filters, reducedMotion }) => {
  const [loading, setLoading] = useState(true);
  const [agentsData, setAgentsData] = useState(null);
  const [sortBy, setSortBy] = useState('quality_score');

  useEffect(() => {
    fetchAgentPerformance();
  }, [filters]);

  // TODO Phase Audit Fix B: wire to real /api/analytics/agent-performance endpoint when backend implements it.
  const fetchAgentPerformance = async () => {
    setLoading(true);
    try {
      // No real endpoint yet — leave data empty so the EmptyState renders.
      setAgentsData([]);
    } catch (error) {
      console.error('Failed to fetch agent performance:', error);
      setAgentsData([]);
    } finally {
      setLoading(false);
    }
  };

  const sortedAgents = [...(agentsData || [])].sort((a, b) => {
    if (sortBy === 'quality_score') return b.quality_score - a.quality_score;
    if (sortBy === 'calls_handled') return b.calls_handled - a.calls_handled;
    if (sortBy === 'fcr') return b.first_call_resolution - a.first_call_resolution;
    return 0;
  });

  const getScoreBadge = (score) => {
    if (score >= 0.9) return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30" data-testid="score-badge-high">Excellent</Badge>;
    if (score >= 0.8) return <Badge className="bg-ink-500 text-ink-500 border-ink-300 dark:border-ink-700" data-testid="score-badge-good">Good</Badge>;
    return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30" data-testid="score-badge-needs-improvement">Needs Improvement</Badge>;
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="agent-performance-matrix-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
              <Users className="w-5 h-5 text-gold" />
              Agent Performance Matrix
            </CardTitle>
            <CardDescription className="text-slate-400 mt-1">
              Individual agent metrics and quality scores
            </CardDescription>
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-1.5 bg-slate-800/50 border border-slate-700 rounded-lg text-sm text-slate-300"
            data-testid="sort-by-select"
          >
            <option value="quality_score">Quality Score</option>
            <option value="calls_handled">Calls Handled</option>
            <option value="fcr">First Call Resolution</option>
          </select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-slate-800/30 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : !agentsData || agentsData.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No data yet"
            description="Data will appear once it flows through."
          />
        ) : (
          <div className="space-y-3">
            {sortedAgents.map((agent, index) => (
              <div
                key={agent.agent_id}
                className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50 hover:bg-slate-800/50 transition-all duration-200"
                style={{
                  animation: reducedMotion ? 'none' : `fadeIn 0.3s ease-out ${index * 0.05}s both`
                }}
                data-testid={`agent-row-${agent.agent_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {index === 0 && (
                      <Award className="w-5 h-5 text-amber-400" data-testid="top-performer-icon" />
                    )}
                    <div>
                      <h4 className="font-medium text-slate-100" data-testid="agent-name">{agent.agent_name}</h4>
                      <p className="text-xs text-slate-400">{agent.calls_handled} calls handled</p>
                    </div>
                  </div>
                  {getScoreBadge(agent.quality_score)}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Quality Score</p>
                    <div className="flex items-center gap-2">
                      <p className="text-lg font-semibold text-slate-100" data-testid="quality-score">{(agent.quality_score * 100).toFixed(0)}%</p>
                      <TrendingUp className="w-4 h-4 text-emerald-400" />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Avg Handle Time</p>
                    <p className="text-lg font-semibold text-slate-100" data-testid="avg-handle-time">{agent.avg_handle_time}s</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 mb-1">FCR Rate</p>
                    <p className="text-lg font-semibold text-slate-100" data-testid="fcr-rate">{(agent.first_call_resolution * 100).toFixed(0)}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 mb-1">Satisfaction</p>
                    <p className="text-lg font-semibold text-slate-100" data-testid="satisfaction-rate">{agent.satisfaction_rate}/5.0</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AgentPerformanceMatrix;